import { useState } from 'react'
import { Box, Button, Text, Heading } from 'theme-ui'
import { motion, useCycle, AnimatePresence } from 'framer-motion'
import Image from 'next/image'

import ChevronDownIcon from '../../icons/ChevronDown'
import ChevronUpIcon from '../../icons/ChevronUp'
import DownloadIcon from '../../icons/Download'
import FadeInImage from '../../FadeInImage'

import CloseButton from './CloseButton'

import {
    iPhoneDevices,
    iPadDevices,
    androidTabletDevices,
    appleWatchDevices,
    androidDevices,
    desktopDevices,
    otherWatchDevices,
} from './devices'

let AnimatedBox = motion(Box)

export default function Download({ token }) {
    const [open, cycleOpen] = useCycle(false, true)

    const tokenIdInt = parseInt(token.token_id)
    const processedTokens = 4000
    const isProcessed = tokenIdInt <= processedTokens

    return (
        <>
            <AnimatePresence>
                {open && (
                    <AnimatedBox
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bg: 'background',
                            mixBlendMode: 'luminosity',
                        }}
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: 0.6,
                        }}
                        exit={{
                            opacity: 0,
                        }}
                        onClick={cycleOpen}
                    />
                )}
                {open && (
                    <AnimatedBox
                        sx={{
                            width: 260,
                            position: 'fixed',
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            bg: 'backgroundBright',
                            flexDirection: 'column',
                            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.05)',
                            overflowY: 'scroll',
                        }}
                        initial={{ right: -260 }}
                        animate={{
                            right: 0,
                        }}
                        exit={{
                            right: -260,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                position: 'sticky',
                                top: 0,
                                p: 4,
                            }}
                        >
                            <CloseButton onClick={cycleOpen} />
                        </Box>

                        {isProcessed ? (
                            <DownloadContent token={token} />
                        ) : (
                            <Box
                                sx={{
                                    px: 4,
                                    flex: 1,
                                    display: 'flex',
                                    pb: 9,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: ['87px', '103px'],
                                        height: ['70px', '83px'],
                                        borderRadius: 2,
                                    }}
                                >
                                    <Image
                                        alt="Illustration of a TinyFaces NFT head with a sad face"
                                        src="/pages/art/empty-placeholder.png"
                                        width={180}
                                        height={178}
                                    />
                                </Box>

                                <Image
                                    src={emptyImage}
                                    srcSet={`${emptyImage} 1x, ${emptyImage2x} 2x`}
                                />
                                <Heading as="h2" sx={{ pt: 3 }}>
                                    Oh no!
                                </Heading>

                                <Text
                                    sx={{
                                        fontSize: 2,
                                        pt: 2,
                                        fontWeight: 'body',
                                        textAlign: 'center',
                                    }}
                                >
                                    Your artwork isn't processed for downloading
                                    yet. Currently about {processedTokens} of
                                    the 4000 artworks are processed.
                                </Text>
                            </Box>
                        )}
                    </AnimatedBox>
                )}
            </AnimatePresence>
            <Button
                onClick={cycleOpen}
                variant="primary"
                sx={{
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'inline-flex' }} mr={2}>
                    <DownloadIcon />
                </Box>
                Download
            </Button>
        </>
    )
}

function DownloadContent({ token }) {
    return (
        <AnimatedBox
            sx={{
                px: 3,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Box sx={{ px: 2, my: 4 }}>
                    <FadeInImage
                        src={`${token.direct_url}?width=200&height=256`}
                        width={100}
                        height={128}
                        borderRadius={0}
                    />
                </Box>
                <Text
                    sx={{
                        px: 2,
                        py: 2,
                        display: 'block',
                        fontWeight: 'heading',
                    }}
                >
                    Artwork
                </Text>
                <DownloadItem
                    token={token}
                    name="Original"
                    type="hq"
                    width={1500}
                    height={1920}
                />
                <DownloadItem
                    token={token}
                    name="With background"
                    type="wide"
                    width={8000}
                    height={5000}
                />
                <DownloadItem
                    token={token}
                    name="Without background"
                    type="transparent"
                    width={3907}
                    height={5000}
                />
                <Box
                    sx={{
                        height: 0.5,
                        bg: 'border',
                        my: 3,
                        mx: 2,
                    }}
                ></Box>
                <Text
                    sx={{
                        px: 2,
                        py: 2,
                        display: 'block',
                        fontWeight: 'heading',
                    }}
                >
                    Wallpapers
                </Text>
                <Box>
                    <DownloadGroup
                        name="Desktop/TV"
                        devices={desktopDevices()}
                        token={token}
                    />
                    <DownloadGroup
                        name="iPhone"
                        devices={iPhoneDevices}
                        token={token}
                    />
                    <DownloadGroup
                        name="iPad"
                        devices={iPadDevices}
                        token={token}
                        flipped
                    />
                    <DownloadGroup
                        name="Apple Watch"
                        devices={appleWatchDevices}
                        token={token}
                    />
                    <DownloadGroup
                        name="Tablet (Android)"
                        devices={androidTabletDevices}
                        token={token}
                    />
                    <DownloadGroup
                        name="Phones (Android)"
                        devices={androidDevices}
                        token={token}
                    />
                    <DownloadGroup
                        name="Other Watches"
                        devices={otherWatchDevices}
                        token={token}
                    />
                </Box>
            </Box>
            <Box sx={{ px: 2, pb: 4, pt: 5, display: 'block' }}>
                <Text
                    sx={{
                        fontSize: 1,
                        fontWeight: 'body',
                        display: 'block',
                    }}
                >
                    Note that downloads can take several seconds to start
                    because of the high quality files
                </Text>
            </Box>
        </AnimatedBox>
    )
}

function DownloadGroup({ name, token, devices, flipped = false }) {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <Box>
            <Box
                sx={{
                    p: 2,
                    fontSize: 2,
                    userSelect: 'none',
                    display: 'flex',
                    borderRadius: 0,
                    cursor: 'pointer',
                    alignItems: 'center',
                    ':hover': {
                        bg: 'background',
                    },
                }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <Text
                    sx={{
                        fontSize: 1,
                        flex: 1,
                    }}
                >
                    {name}
                </Text>

                {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Box>

            {isOpen &&
                devices.map((device) => (
                    <DownloadItem
                        token={token}
                        name={device.name}
                        width={flipped ? device.height : device.width}
                        height={flipped ? device.width : device.height}
                        type="wide"
                    />
                ))}
        </Box>
    )
}

function DownloadItem({ token, name, width, height, type }) {
    var url = `${token.direct_url}?download=true&type=${type}`

    if (width) {
        url += `&width=${width}`
    }

    if (height) {
        url += `&height=${height}`
    }
    return (
        <Box
            as="a"
            href={url}
            download
            sx={{
                display: 'flex',
                borderRadius: 0,
                color: 'text',
                p: 2,
                alignItems: 'center',
                textDecoration: 'none',
                fontSize: 1,
                justifyContent: 'center',
                fontWeight: 'body',
                ':hover': {
                    bg: 'background',
                    '.icon': {
                        display: 'flex',
                    },
                },
            }}
        >
            <Box sx={{ flex: 1 }}>{name}</Box>
            <Box className="icon" sx={{ display: 'none' }}>
                <DownloadIcon />
            </Box>
        </Box>
    )
}
